import { Box, Button, Grid, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Overlay, createOverlay, useOverlayContext } from '~/components/Overlay'
import { type InAppNotificationResponse, useMarkInAppNotificationsReadByIdsMutation } from '~/data/api/client/workflows'
import { useMarkInAppNotificationReadMutation } from '~/data/api/client/workflows/enhancedEndpoints'
import useFormatters from '~/data/formatters'
import routeDirectory from '~/navigation/routes'
import { useSnackbarControls } from '~/store/slices/display'
import { removeOverlayNotifications, setOverlayOpen } from '~/store/slices/notifications'
import { usePaletteColor } from '~/theme/hooks'

import PriorityPill from './PriorityPill'

type NotificationOverlayProps = {
  rows: InAppNotificationResponse[]
  onRowsAcknowledged: (ids: string[]) => void
}

const NotificationsOverlay = createOverlay(({ rows, onRowsAcknowledged }: NotificationOverlayProps) => {
  const t = useTranslations('notifications.NotificationsOverlay')

  const router = useRouter()
  const pathname = usePathname()
  const { notifications: notificationsRoute } = routeDirectory
  const dispatch = useDispatch()

  const [markInAppRead] = useMarkInAppNotificationReadMutation()
  const [markInAppReadByIds] = useMarkInAppNotificationsReadByIdsMutation()
  const { openSnackbar } = useSnackbarControls()
  const [isAcknowledgingAll, setIsAcknowledgingAll] = useState(false)
  const isMultiple = rows.length > 1

  const [selectedId, setSelectedId] = useState<string>()
  const lastIndexRef = useRef(0)
  const selectedIndex = rows.findIndex(({ id }) => id === selectedId)
  const currentNotificationIndex = selectedIndex >= 0 ? selectedIndex : Math.min(lastIndexRef.current, rows.length - 1)
  const currentRow = rows[currentNotificationIndex]

  const currentId = currentRow?.id

  useEffect(() => {
    lastIndexRef.current = Math.max(currentNotificationIndex, 0)
  }, [currentNotificationIndex])

  useEffect(() => {
    if (currentId && currentId !== selectedId) setSelectedId(currentId)
  }, [currentId, selectedId])

  const onAcknowledge = () => {
    if (!currentRow) return

    dispatch(removeOverlayNotifications([currentRow.id]))
    markInAppRead({ notificationId: currentRow.id, markReadRequest: { read: true } })
  }
  const { closeOverlay } = useOverlayContext()

  const onAcknowledgeAll = async () => {
    const ids = rows.map(({ id }) => id)

    setIsAcknowledgingAll(true)
    try {
      await markInAppReadByIds({ markReadByIdsRequest: { notificationIds: ids } }).unwrap()
      dispatch(removeOverlayNotifications(ids))
      onRowsAcknowledged(ids)
    } catch {
      // Rows stay visible so the user can retry; the overlay closes itself once no rows remain.
      openSnackbar(t('acknowledgeAllError'), { severity: 'error' })
    } finally {
      setIsAcknowledgingAll(false)
    }
  }

  const canViewAll = pathname !== notificationsRoute

  return (
    <Overlay fullWidth maxWidth="md">
      <Overlay.Header title={isMultiple ? t('title', { count: rows.length }) : rows[0]?.title ?? ''}>
        {!isMultiple && <PriorityPill priority={rows[0]?.priority} />}
      </Overlay.Header>
      <Overlay.Content>
        <Grid container spacing={2}>
          {isMultiple && (
            <Grid item xs={12}>
              <Tabs
                onChange={(_, index) => setSelectedId(rows[index]?.id)}
                scrollButtons="auto"
                value={currentNotificationIndex}
                variant="scrollable"
              >
                {rows.map(({ id, title }) => (
                  <Tab key={id} label={title.length > 30 ? `${title.slice(0, 27)}...` : title} />
                ))}
              </Tabs>
            </Grid>
          )}
          {currentRow && <WorkflowOverlayBody notification={currentRow} />}
        </Grid>
      </Overlay.Content>
      <Overlay.Actions>
        {canViewAll && (
          <Button
            onClick={() => {
              router.push(notificationsRoute)
              dispatch(setOverlayOpen(false))
              closeOverlay()
            }}
            variant="outlined"
          >
            {t('viewAll')}
          </Button>
        )}
        {isMultiple && (
          <Button disabled={isAcknowledgingAll} onClick={onAcknowledgeAll} variant="outlined">
            {t('acknowledgeAll', { count: rows.length })}
          </Button>
        )}
        <Button onClick={onAcknowledge} variant="contained">
          {t('acknowledge')}
        </Button>
      </Overlay.Actions>
    </Overlay>
  )
})

type WorkflowOverlayBodyProps = {
  notification: InAppNotificationResponse
}

const WorkflowOverlayBody = ({ notification }: WorkflowOverlayBodyProps) => {
  const t = useTranslations('notifications.NotificationsOverlay')
  const format = useFormatters()
  const backgroundColor = usePaletteColor('primary', 0.1)

  return (
    <Grid container item spacing={2} xs={12}>
      <Grid item xs={12}>
        <Box sx={{ backgroundColor, p: 2 }}>
          <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="subtitle1">
            {notification.body}
          </Typography>
        </Box>
      </Grid>
      <Grid item md={3} sm={6} xs={12}>
        <Stack>
          <Typography variant="caption">{t('labels_received')}</Typography>
          <Typography variant="subtitle1">
            {format.dateTime(new Date(notification.createdAt), 'dateDotTime')}
          </Typography>
        </Stack>
      </Grid>
      {notification.workflowName && (
        <Grid item md={9} sm={6} xs={12}>
          <Stack>
            <Typography variant="caption">{t('labels_workflow')}</Typography>
            <Typography noWrap variant="subtitle1">
              {notification.workflowName}
            </Typography>
          </Stack>
        </Grid>
      )}
    </Grid>
  )
}

export default NotificationsOverlay
